import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { BatchRow, StockMovementRow, StockOnHandRow, StockStatus } from '@sellokart/shared';
import { PageHeader } from '@/components/PageHeader';
import { AsyncSection } from '@/components/AsyncSection';
import { ReportFilterBar } from '@/components/ReportFilterBar';
import { ReportColumnsButton, ReportFiltersButton, ReportSearchField, ReportToolbar } from '@/components/ReportToolbar';
import { applyReportLayout, DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { TablePager } from '@/components/TablePager';
import { ExportMenu } from '@/components/ExportMenu';
import { Badge } from '@/components/Badge';
import type { BadgeTone } from '@/components/Badge';
import {
  downloadInventoryExport,
  fetchInventoryBatches,
  fetchInventoryMovement,
  fetchInventoryStock,
  reportKeys,
} from '@/features/reports/api';
import type { ExportFormat } from '@/features/reports/api';
import { useReportFilters } from '@/features/reports/useReportFilters';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { serialiseFilters, type ReportFilterRow } from '@/features/reports/reportFilters';
import type { ReportFilters } from '@/features/reports/useReportFilters';
import { usePortal } from '@/features/portal/PortalContext';

const PAGE_SIZE = 25;

const money = (value: number) => value.toFixed(2);
const qty = (value: number) => value.toFixed(2);

function formatDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

const EXPIRY_TONE: Record<BatchRow['expiryStatus'], BadgeTone> = {
  ok: 'neutral',
  expiring: 'warning',
  expired: 'danger',
};

const EXPIRY_LABEL: Record<BatchRow['expiryStatus'], string> = {
  ok: 'Ok',
  expiring: 'Expiring',
  expired: 'Expired',
};

// Two independent tables with different shapes, so each keeps its own
// layout rather than sharing one ambiguous column picker.
const BATCH_TOGGLEABLE = ['batchNo', 'expiryDate', 'quantity', 'cost', 'expiryStatus'] as const;
const BATCH_LABELS: Record<string, string> = {
  batchNo: 'Batch #',
  expiryDate: 'Expiry',
  quantity: 'Qty',
  cost: 'Cost',
  expiryStatus: 'Status',
};
const BATCH_FILTER_FIELDS = ['Product', 'Batch #', 'Status'] as const;
const MOVEMENT_FILTER_FIELDS = ['Product', 'Reason', 'Adjusted by'] as const;

const STOCK_TONE: Record<StockStatus, BadgeTone> = {
  ok: 'success',
  low: 'warning',
  out: 'danger',
  // Grey on purpose: a Service has no stock to be short of, so colouring it
  // would put it in the same visual bucket as a product that has run out.
  untracked: 'neutral',
};

const STOCK_LABEL: Record<StockStatus, string> = {
  ok: 'In stock',
  low: 'Low',
  out: 'Out of stock',
  untracked: 'Not tracked',
};

const STOCK_TOGGLEABLE = ['categoryName', 'barcode', 'unit', 'status', 'quantity', 'reorderLevel', 'sellingPrice', 'stockValue'] as const;
const STOCK_LABELS: Record<string, string> = {
  categoryName: 'Category',
  barcode: 'Barcode',
  unit: 'Unit',
  status: 'Status',
  quantity: 'Qty',
  reorderLevel: 'Reorder at',
  sellingPrice: 'Price',
  stockValue: 'Stock value',
};
const STOCK_FILTER_FIELDS = ['Product', 'Category', 'Barcode', 'Status'] as const;

const MOVEMENT_TOGGLEABLE = ['oldQty', 'newQty', 'delta', 'reason', 'adjustedBy'] as const;
const MOVEMENT_LABELS: Record<string, string> = {
  oldQty: 'Old qty',
  newQty: 'New qty',
  delta: 'Delta',
  reason: 'Reason',
  adjustedBy: 'Adjusted by',
};

const BATCH_COLUMNS: Column<BatchRow>[] = [
  { key: 'productName', header: 'Product', pinned: true, cell: (row) => row.productName },
  { key: 'batchNo', header: 'Batch #', cell: (row) => row.batchNo },
  { key: 'expiryDate', header: 'Expiry', cell: (row) => formatDate(row.expiryDate) },
  { key: 'quantity', header: 'Qty', align: 'right', cell: (row) => qty(row.quantity) },
  { key: 'cost', header: 'Cost', align: 'right', cell: (row) => money(row.cost) },
  {
    key: 'expiryStatus',
    header: 'Status',
    cell: (row) => (
      <Badge tone={EXPIRY_TONE[row.expiryStatus]} dot>
        {EXPIRY_LABEL[row.expiryStatus]}
      </Badge>
    ),
  },
];

/** A Service has no stock figure — a dash, not a zero it never recorded. */
const untracked = <span className="text-slate-300">—</span>;

const STOCK_COLUMNS: Column<StockOnHandRow>[] = [
  { key: 'productName', header: 'Product', pinned: true, cell: (row) => row.productName },
  { key: 'categoryName', header: 'Category', cell: (row) => row.categoryName || '—' },
  { key: 'barcode', header: 'Barcode', visibility: 'hidden sm:table-cell', cell: (row) => row.barcode || '—' },
  { key: 'unit', header: 'Unit', visibility: 'hidden sm:table-cell', cell: (row) => row.unit || '—' },
  {
    key: 'status',
    header: 'Status',
    cell: (row) => (
      <Badge tone={STOCK_TONE[row.status]} dot>
        {STOCK_LABEL[row.status]}
      </Badge>
    ),
  },
  {
    key: 'quantity',
    header: 'Qty',
    align: 'right',
    cell: (row) => (row.status === 'untracked' ? untracked : qty(row.quantity)),
  },
  {
    key: 'reorderLevel',
    header: 'Reorder at',
    align: 'right',
    cell: (row) => (row.status === 'untracked' ? untracked : qty(row.reorderLevel)),
  },
  { key: 'sellingPrice', header: 'Price', align: 'right', cell: (row) => money(row.sellingPrice) },
  {
    key: 'stockValue',
    header: 'Stock value',
    align: 'right',
    cell: (row) => (row.status === 'untracked' ? untracked : money(row.stockValue)),
  },
];

const MOVEMENT_COLUMNS: Column<StockMovementRow>[] = [
  { key: 'productName', header: 'Product', pinned: true, cell: (row) => row.productName },
  { key: 'oldQty', header: 'Old qty', align: 'right', cell: (row) => qty(row.oldQty) },
  { key: 'newQty', header: 'New qty', align: 'right', cell: (row) => qty(row.newQty) },
  { key: 'delta', header: 'Delta', align: 'right', cell: (row) => qty(row.delta) },
  { key: 'reason', header: 'Reason', visibility: 'hidden sm:table-cell', cell: (row) => row.reason },
  { key: 'adjustedBy', header: 'Adjusted by', visibility: 'hidden sm:table-cell', cell: (row) => row.adjustedBy },
];

/**
 * Batches are point-in-time stock on hand (no date range — only branch), while
 * stock movement is a date-ranged history — the two endpoints take different
 * query shapes, so this page has two independent filter-bar renderings for
 * the two sections rather than sharing one `ReportFilterBar` call.
 *
 * Both lists are server-paginated: a tenant's batches/adjustments can outgrow
 * one page just as easily as sales history does, and search/filters are
 * applied server-side for the same reason — narrowing only the fetched page
 * would silently miss rows once the data set needs pagination at all.
 */
export function InventoryReportPage() {
  const { profile } = usePortal();
  const [filters, setFilters] = useReportFilters();
  const [stockSearch, setStockSearch] = useState('');
  const [stockPage, setStockPage] = useState(1);
  const [stockFilters, setStockFilters] = useState<ReportFilterRow[]>([]);
  const [stockMatchAny, setStockMatchAny] = useState(true);
  const [batchSearch, setBatchSearch] = useState('');
  const [batchPage, setBatchPage] = useState(1);
  const [movementSearch, setMovementSearch] = useState('');
  const [movementPage, setMovementPage] = useState(1);
  const [batchFilters, setBatchFilters] = useState<ReportFilterRow[]>([]);
  const [batchMatchAny, setBatchMatchAny] = useState(true);
  const [movementFilters, setMovementFilters] = useState<ReportFilterRow[]>([]);
  const [movementMatchAny, setMovementMatchAny] = useState(true);
  const stockLayout = useReportLayout('inventory-stock', STOCK_TOGGLEABLE);
  const stockColumns = applyReportLayout(STOCK_COLUMNS, stockLayout);
  const batchLayout = useReportLayout('inventory-batches', BATCH_TOGGLEABLE);
  const movementLayout = useReportLayout('inventory-movement', MOVEMENT_TOGGLEABLE);
  const batchColumns = applyReportLayout(BATCH_COLUMNS, batchLayout);
  const movementColumns = applyReportLayout(MOVEMENT_COLUMNS, movementLayout);

  const stockQueryFilters: ReportFilters = {
    ...filters,
    search: stockSearch,
    filters: serialiseFilters(stockFilters) ?? '',
    matchAny: stockMatchAny ? '' : 'false',
  };
  const batchQueryFilters: ReportFilters = {
    ...filters,
    search: batchSearch,
    filters: serialiseFilters(batchFilters) ?? '',
    matchAny: batchMatchAny ? '' : 'false',
  };
  const movementQueryFilters: ReportFilters = {
    ...filters,
    search: movementSearch,
    filters: serialiseFilters(movementFilters) ?? '',
    matchAny: movementMatchAny ? '' : 'false',
  };

  const stockQuery = useQuery({
    queryKey: reportKeys.inventoryStock(stockQueryFilters, stockPage, PAGE_SIZE),
    queryFn: ({ signal }) => fetchInventoryStock(stockQueryFilters, stockPage, PAGE_SIZE, signal),
  });
  const batchesQuery = useQuery({
    queryKey: reportKeys.inventoryBatches(batchQueryFilters, batchPage, PAGE_SIZE),
    queryFn: ({ signal }) => fetchInventoryBatches(batchQueryFilters, batchPage, PAGE_SIZE, signal),
  });
  const movementQuery = useQuery({
    queryKey: reportKeys.inventoryMovement(movementQueryFilters, movementPage, PAGE_SIZE),
    queryFn: ({ signal }) => fetchInventoryMovement(movementQueryFilters, movementPage, PAGE_SIZE, signal),
  });

  return (
    <div>
      <PageHeader
        title="Inventory report"
        description="Stock on hand, batches and expiry, and recent stock adjustments."
      />

      <section className="mb-8">
        <div className="mb-3 flex items-center justify-between gap-4">
          <h2 className="text-sm font-semibold text-slate-900">Stock on hand</h2>
        </div>
        <ReportFilterBar
          filters={filters}
          onChange={(next) => {
            setStockPage(1);
            setBatchPage(1);
            setMovementPage(1);
            setFilters(next);
          }}
          branches={profile?.branches ?? []}
          hideDateRange
          idPrefix="report-filter-stock"
          sectionLabel="stock on hand"
        />
        <ReportToolbar>
          <ReportSearchField
            value={stockSearch}
            onChange={(next) => {
              setStockPage(1);
              setStockSearch(next);
            }}
            placeholder="Search product, category or barcode"
          />
          <ReportFiltersButton
            fields={STOCK_FILTER_FIELDS}
            applied={stockFilters}
            matchAny={stockMatchAny}
            onApply={(rows, matchAny) => {
              setStockPage(1);
              setStockFilters(rows);
              setStockMatchAny(matchAny);
            }}
            placeholderFor={(field) =>
              field === 'Status'
                ? 'ok / low / out / untracked'
                : field === 'Category'
                  ? 'category'
                  : field === 'Barcode'
                    ? 'barcode'
                    : 'product'
            }
          />
          <ReportColumnsButton layout={stockLayout} labels={STOCK_LABELS} />
          <div className="ml-auto">
            <ExportMenu onExport={(format: ExportFormat) => downloadInventoryExport('stock', format, stockQueryFilters)} />
          </div>
        </ReportToolbar>
        <AsyncSection
          state={stockQuery}
          isEmpty={(paged) => paged.items.length === 0}
          emptyTitle="No products yet"
          skeleton={<TableSkeleton columns={9} />}
        >
          {(paged) => (
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
              <DataTable
                caption="Stock on hand per product, with reorder level and value"
                layout={stockLayout}
                columns={stockColumns}
                rows={paged.items}
                rowKey={(row, index) => `${row.productName}-${index}`}
              />
              <TablePager
                page={paged.page}
                pageSize={paged.pageSize}
                total={paged.total}
                onPageChange={setStockPage}
                busy={stockQuery.isFetching}
                noun="product"
              />
            </div>
          )}
        </AsyncSection>
      </section>

      <section className="mb-8">
        <div className="mb-3 flex items-center justify-between gap-4">
          <h2 className="text-sm font-semibold text-slate-900">Batches &amp; expiry</h2>
        </div>
        <ReportFilterBar
          filters={filters}
          onChange={(next) => {
            setBatchPage(1);
            setMovementPage(1);
            setFilters(next);
          }}
          branches={profile?.branches ?? []}
          hideDateRange
          idPrefix="report-filter-batches"
          sectionLabel="batches"
        />
        <ReportToolbar>
          <ReportSearchField
            value={batchSearch}
            onChange={(next) => {
              setBatchPage(1);
              setBatchSearch(next);
            }}
            placeholder="Search product or batch #"
          />
          <ReportFiltersButton
            fields={BATCH_FILTER_FIELDS}
            applied={batchFilters}
            matchAny={batchMatchAny}
            onApply={(rows, matchAny) => {
              setBatchPage(1);
              setBatchFilters(rows);
              setBatchMatchAny(matchAny);
            }}
            placeholderFor={(field) =>
              field === 'Status' ? 'ok / expiring / expired' : field === 'Batch #' ? 'batch #' : 'product'
            }
          />
          <ReportColumnsButton layout={batchLayout} labels={BATCH_LABELS} />
          <div className="ml-auto">
            <ExportMenu onExport={(format: ExportFormat) => downloadInventoryExport('batches', format, batchQueryFilters)} />
          </div>
        </ReportToolbar>
        <AsyncSection
          state={batchesQuery}
          isEmpty={(paged) => paged.items.length === 0}
          emptyTitle="No batches on hand"
          skeleton={<TableSkeleton columns={6} />}
        >
          {(paged) => (
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
              <DataTable
                caption="Batches and expiry"
                layout={batchLayout}
                columns={batchColumns}
                rows={paged.items}
                rowKey={(row, index) => `${row.batchNo}-${index}`}
              />
              <TablePager
                page={paged.page}
                pageSize={paged.pageSize}
                total={paged.total}
                onPageChange={setBatchPage}
                busy={batchesQuery.isFetching}
                noun="batch"
              />
            </div>
          )}
        </AsyncSection>
      </section>

      <section>
        <h2 className="mb-3 text-sm font-semibold text-slate-900">Stock movement</h2>
        <ReportFilterBar
          filters={filters}
          onChange={(next) => {
            setBatchPage(1);
            setMovementPage(1);
            setFilters(next);
          }}
          branches={profile?.branches ?? []}
          idPrefix="report-filter-movement"
          sectionLabel="stock movement"
        />
        <ReportToolbar>
          <ReportSearchField
            value={movementSearch}
            onChange={(next) => {
              setMovementPage(1);
              setMovementSearch(next);
            }}
            placeholder="Search product, reason or staff"
          />
          <ReportFiltersButton
            fields={MOVEMENT_FILTER_FIELDS}
            applied={movementFilters}
            matchAny={movementMatchAny}
            onApply={(rows, matchAny) => {
              setMovementPage(1);
              setMovementFilters(rows);
              setMovementMatchAny(matchAny);
            }}
            placeholderFor={(field) =>
              field === 'Reason' ? 'reason' : field === 'Adjusted by' ? 'staff name' : 'product'
            }
          />
          <ReportColumnsButton layout={movementLayout} labels={MOVEMENT_LABELS} />
          <div className="ml-auto">
            <ExportMenu onExport={(format: ExportFormat) => downloadInventoryExport('movement', format, movementQueryFilters)} />
          </div>
        </ReportToolbar>
        <AsyncSection
          state={movementQuery}
          isEmpty={(paged) => paged.items.length === 0}
          emptyTitle="No stock adjustments in this window"
          skeleton={<TableSkeleton columns={5} />}
        >
          {(paged) => (
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
              <DataTable
                caption="Stock movement"
                layout={movementLayout}
                columns={movementColumns}
                rows={paged.items}
                rowKey={(row, index) => `${row.productName}-${index}`}
              />
              <TablePager
                page={paged.page}
                pageSize={paged.pageSize}
                total={paged.total}
                onPageChange={setMovementPage}
                busy={movementQuery.isFetching}
                noun="adjustment"
              />
            </div>
          )}
        </AsyncSection>
      </section>
    </div>
  );
}
